import {
  AutoscaleNodeStateResponse,
  AutoscaleNodeStateEntry,
  NodeStatus,
  NodeEvent,
  ScalingEvent,
} from '../types';

/**
 * Parse autoscale store key format: SubGroupRoleKey|nodeGroupName|subGroupName|nodeRoleName
 */
export function parseStoreKey(
  key: string
): { nodeGroupName: string; subGroupName: string; nodeRoleName: string } | null {
  const parts = key.split('|');
  if (parts.length >= 4 && parts[0] === 'SubGroupRoleKey') {
    return { nodeGroupName: parts[1], subGroupName: parts[2], nodeRoleName: parts[3] };
  }
  return null;
}

/**
 * Extract unique node group names from autoscale node-state response (sorted).
 */
export function getNodeGroupNamesFromAutoscaleResponse(
  response: AutoscaleNodeStateResponse
): string[] {
  const names = new Set<string>();
  Object.keys(response || {}).forEach((storeKey) => {
    const parsed = parseStoreKey(storeKey);
    if (parsed) names.add(parsed.nodeGroupName);
  });
  return Array.from(names).sort();
}

export type OnNodeMetaCallback = (
  nodeId: string,
  storeKey: string,
  entry: AutoscaleNodeStateEntry
) => void;

/**
 * Convert autoscale node-state response to NodeStatus[] for a single node group.
 * Optionally call onNodeMeta for each node (e.g. to populate nodeIdToMeta ref in NodeGroupStatus).
 */
export function normalizeAutoscaleToNodeStatus(
  response: AutoscaleNodeStateResponse,
  groupName: string,
  onNodeMeta?: OnNodeMetaCallback
): NodeStatus[] {
  const list: NodeStatus[] = [];
  Object.entries(response || {}).forEach(([storeKey, group]) => {
    const parsed = parseStoreKey(storeKey);
    if (!parsed || parsed.nodeGroupName !== groupName) return;
    const nodesById = group?.nodesById || {};
    Object.entries(nodesById).forEach(([, entry]) => {
      const ev = entry.nodeEvent;
      const nodeGroupName = parsed.nodeGroupName;
      const subGroupName = parsed.subGroupName;
      const nodeRoleName = parsed.nodeRoleName;
      const state = (entry.scalingState || 'UNKNOWN') as ScalingEvent['state'];
      const timestamp = entry.lastNodeScalingStateUpdateTime ?? ev?.timestamp ?? 0;
      const nodeEvent: NodeEvent = ev
        ? {
            nodeId: ev.nodeId,
            nodeGroupName: ev.nodeGroupName,
            subGroupName: ev.subGroupName,
            nodeRoleName: ev.nodeRoleName as NodeEvent['nodeRoleName'],
            parents: ev.parents || [],
            publicIp: ev.publicIp || '',
            privateIp: ev.privateIp || '',
            timestamp: ev.timestamp || 0,
            publisherCount: ev.publisherCount ?? 0,
            subscriberCount: ev.subscriberCount ?? 0,
            clientCount: ev.clientCount ?? 0,
            dataClientCount: ev.dataClientCount ?? 0,
            restreamerCount: ev.restreamerCount ?? 0,
            childClusterStreamCount: ev.childClusterStreamCount ?? 0,
            postProcessorCount: ev.postProcessorCount ?? 0,
          }
        : {
            nodeId: entry.nodeId,
            nodeGroupName,
            subGroupName,
            nodeRoleName: nodeRoleName as NodeEvent['nodeRoleName'],
            parents: [],
            publicIp: '',
            privateIp: '',
            timestamp: 0,
            publisherCount: 0,
            subscriberCount: 0,
            clientCount: 0,
            dataClientCount: 0,
            restreamerCount: 0,
            childClusterStreamCount: 0,
            postProcessorCount: 0,
          };
      const scalingEvent: ScalingEvent = {
        nodeGroupName,
        subGroupName,
        nodeRoleName: nodeRoleName as ScalingEvent['nodeRoleName'],
        nodeId: entry.nodeId,
        state: state as ScalingEvent['state'],
        timestamp,
      };
      list.push({ nodeEvent, scalingEvent });
      onNodeMeta?.(entry.nodeId, storeKey, entry);
    });
  });
  return list;
}
